import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Phone, Mail, MessageSquare, Clock, Search, ChevronRight,
  CheckCircle2, RefreshCw, SkipForward, Info, Calendar, PhoneCall,
  UserX, Zap, Filter,
} from "lucide-react";
import SelfRegisteredBadge from "@/components/SelfRegisteredBadge";
import { useSmartLeads } from "@/hooks/useSmartLeads";
import { SmartQueueBar } from "@/components/smartleads/SmartQueueBar";
import { AiCardStrip } from "@/components/smartleads/AiCardStrip";
import { SmartDraftDialog } from "@/components/smartleads/SmartDraftDialog";
import { LeadTextDrafts } from "@/components/smartleads/LeadTextDrafts";
import { Sparkles, ExternalLink } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Grace window (ms) during which a queue action can be undone before it commits.
const UNDO_MS = 6000;
const PAGE_SIZE = 20;

const COLUMNS = [
  { key: "high_priority", label: "High Priority", dot: "bg-red-500", text: "text-red-700", chip: "bg-red-100 text-red-700", reason: "bg-red-50 text-red-700 border-red-100" },
  { key: "medium_priority", label: "Medium Priority", dot: "bg-amber-500", text: "text-amber-700", chip: "bg-amber-100 text-amber-700", reason: "bg-amber-50 text-amber-700 border-amber-100" },
  { key: "low_priority", label: "Low Priority", dot: "bg-blue-500", text: "text-blue-700", chip: "bg-blue-100 text-blue-700", reason: "bg-blue-50 text-blue-700 border-blue-100" },
];

const withCounts = (q) => {
  const h = (q.high_priority || []).length;
  const m = (q.medium_priority || []).length;
  const l = (q.low_priority || []).length;
  return { ...q, high_count: h, medium_count: m, low_count: l, total_count: h + m + l };
};

const scoreCls = (s) =>
  s >= 75 ? "text-green-600 bg-green-100" : s >= 50 ? "text-amber-600 bg-amber-100" : "text-slate-500 bg-slate-100";

// ── Preferred-time → reachable-now ──────────────────────────────────────
const TIME_WINDOWS = { morning: [8, 12], afternoon: [12, 17], evening: [17, 21] };

const parseWindow = (raw) => {
  const p = raw.toLowerCase();
  if (p.includes("morning") || /\bam\b/.test(p)) return TIME_WINDOWS.morning;
  if (p.includes("afternoon") || p.includes("noon")) return TIME_WINDOWS.afternoon;
  if (p.includes("evening") || p.includes("night") || /\bpm\b/.test(p)) return TIME_WINDOWS.evening;
  const m = p.match(/after\s*(\d{1,2})\s*(am|pm)?/);
  if (m) { let h = parseInt(m[1], 10); if (m[2] === "pm" && h < 12) h += 12; return [h, 21]; }
  return null;
};

const prefInfo = (pref) => {
  const raw = (pref || "").trim();
  if (!raw || /^(none|anytime|any)$/i.test(raw)) return { label: "Anytime", good: true };
  const w = parseWindow(raw);
  if (!w) return { label: raw, good: true };
  const h = new Date().getHours();
  return { label: raw, good: h >= w[0] && h < w[1] };
};

const fmtLast = (iso) => {
  if (!iso) return "Never";
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (days <= 0) return "today";
  if (days === 1) return "1d ago";
  if (days < 30) return `${days}d ago`;
  return `${Math.floor(days / 30)}mo ago`;
};

// ── Compact board card ──────────────────────────────────────────────────
const BoardCard = ({ lead, tone, onAction, onOpen, smart }) => {
  const initial = lead.name?.charAt(0)?.toUpperCase() || "?";
  const pref = prefInfo(lead.preferred_time);
  const svc = [lead.pet_name, lead.service].filter(Boolean).join(" · ");
  const stop = (e) => e.stopPropagation();

  return (
    <div
      className="group card bg-white rounded-xl border border-slate-200 p-2.5 cursor-pointer relative overflow-hidden hover:shadow-lg hover:border-slate-300 transition-all"
      onClick={() => onOpen(lead)}
      data-testid={`queue-lead-${lead.id}`}
    >
      <div className={`absolute top-0 left-0 h-full w-1 ${tone.dot}`} />
      <ChevronRight className="w-4 h-4 text-slate-300 absolute right-2 top-2.5 opacity-0 group-hover:opacity-100 transition-opacity" />
      <div className="pl-2 pr-3">
        {/* name row */}
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-[11px] font-bold flex-shrink-0">
              {initial}
            </div>
            <div className="min-w-0">
              <p className="font-bold text-slate-900 text-sm leading-tight truncate">{lead.name || "Unknown"}</p>
              <p className="text-[11px] text-slate-400 truncate">{lead.email || lead.phone || "No contact info"}</p>
            </div>
          </div>
          <span className={`text-[11px] font-bold rounded px-1.5 py-0.5 flex-shrink-0 mr-3 ${scoreCls(lead.score || 0)}`}>
            {lead.score || 0}
          </span>
        </div>

        {/* gingr + service chips */}
        <div className="flex items-center gap-1.5 mt-2 flex-wrap">
          {lead.self_registered && (
            <SelfRegisteredBadge size="sm" withPulse={tone && tone.dot === "bg-red-500"} />
          )}
          {lead.has_gingr ? (
            <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-100 rounded px-1.5 py-0.5" data-testid="badge-in-gingr">
              <CheckCircle2 className="w-3 h-3" /> In Gingr
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 text-[10px] font-bold text-slate-500 bg-slate-100 rounded px-1.5 py-0.5" data-testid="badge-no-gingr">
              <UserX className="w-3 h-3" /> No Gingr
            </span>
          )}
          {lead.has_gingr && lead.gingr_owner_id && smart.gingrUrl && (
            <a href={`${smart.gingrUrl}/owners/view/id/${lead.gingr_owner_id}`} target="_blank" rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()} title="Open in Gingr"
              className="inline-flex items-center gap-0.5 text-[10px] font-bold text-emerald-700 hover:text-emerald-900 hover:bg-emerald-50 rounded px-1 py-0.5"
              data-testid={`open-gingr-${lead.id}`}>
              <ExternalLink className="w-3 h-3" /> Gingr
            </a>
          )}
          {svc && <span className="text-[10px] font-semibold text-purple-700 bg-purple-50 rounded px-1.5 py-0.5 truncate max-w-[140px]">{svc}</span>}
        </div>

        {/* reason */}
        {lead.reason && (
          <div className={`mt-2 inline-flex items-center gap-1 text-[11px] font-semibold border rounded-md px-1.5 py-0.5 ${tone.reason}`}>
            <Info className="w-3 h-3" /> {lead.reason}
          </div>
        )}

        {/* meta line: age · last contact · contacts · preferred time */}
        <div className="flex items-center gap-2.5 mt-2 text-[11px] text-slate-500 flex-wrap">
          <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> Age {lead.days_since_created ?? 0}d</span>
          <span className="flex items-center gap-1"><PhoneCall className="w-3 h-3" /> Last {fmtLast(lead.last_contact_at)}</span>
          <span
            className={`flex items-center gap-1 font-semibold ${
              (lead.outreach_count ?? 0) >= 3 ? "text-amber-700" : "text-slate-500"
            }`}
            title="Logged outreach attempts (call / email / SMS)"
            data-testid={`queue-contacts-${lead.id}`}
          >
            <MessageSquare className="w-3 h-3" /> {lead.outreach_count ?? 0} contacts
          </span>
          <span className={`flex items-center gap-1 font-semibold ${pref.good ? "text-emerald-700" : "text-amber-600"}`} data-testid="pref-time">
            <span className={`w-2 h-2 rounded-full ${pref.good ? "bg-emerald-500" : "bg-amber-400"}`} />
            {pref.label}
          </span>
        </div>

        <AiCardStrip id={lead.id} ai={smart.ai[lead.id]} outboxStatus={smart.ob[lead.id]} selected={smart.sel[lead.id]}
          onToggle={smart.toggle} onDraft={smart.draft} onResume={smart.resume} />

        {/* actions */}
        <div className="flex items-center justify-end gap-1 mt-2 pt-2 border-t border-slate-100 opacity-60 group-hover:opacity-100 transition-opacity" onClick={stop}>
          <button className="w-7 h-7 rounded-md border border-blue-200 text-blue-600 hover:bg-blue-50 flex items-center justify-center" title="Call" onClick={() => onAction(lead, "call")} data-testid={`queue-action-call-${lead.id}`}>
            <Phone className="w-3.5 h-3.5" />
          </button>
          <button className="w-7 h-7 rounded-md border border-green-200 text-green-600 hover:bg-green-50 flex items-center justify-center" title="Email" onClick={() => onAction(lead, "email")} data-testid={`queue-action-email-${lead.id}`}>
            <Mail className="w-3.5 h-3.5" />
          </button>
          <button className="w-7 h-7 rounded-md border border-purple-200 text-purple-600 hover:bg-purple-50 flex items-center justify-center" title="SMS" onClick={() => onAction(lead, "sms")} data-testid={`queue-action-sms-${lead.id}`}>
            <MessageSquare className="w-3.5 h-3.5" />
          </button>
          <button className="w-7 h-7 rounded-md border border-slate-200 text-slate-500 hover:bg-slate-50 flex items-center justify-center" title="Skip" onClick={() => onAction(lead, "skip")} data-testid={`queue-action-skip-${lead.id}`}>
            <SkipForward className="w-3.5 h-3.5" />
          </button>
          <button className="px-2 h-7 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-[11px] font-bold flex items-center gap-1" title="Complete" onClick={() => onAction(lead, "complete")} data-testid={`queue-action-complete-${lead.id}`}>
            <CheckCircle2 className="w-3.5 h-3.5" /> Done
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Scrollable column with incremental (infinite) rendering ─────────────
const BoardColumn = ({ col, leads, visible, onScrollLoad, colRef, onAction, onOpen, smart }) => {
  const shown = leads.slice(0, visible);
  return (
    <div className="bg-slate-100/70 rounded-2xl p-3 flex flex-col" data-testid={`queue-col-${col.key}`}>
      <div className="flex items-center justify-between px-1 mb-3">
        <div className="flex items-center gap-2">
          <span className={`w-2.5 h-2.5 rounded-full ${col.dot}`} />
          <h2 className="text-lg font-extrabold text-gray-900 tracking-tight" style={{ fontFamily: "Manrope" }}>{col.label}</h2>
        </div>
        <span className={`text-xs font-bold rounded-full px-2 py-0.5 ${col.chip}`} style={{ fontFamily: "JetBrains Mono, monospace" }} data-testid={`queue-count-${col.key}`}>{leads.length}</span>
      </div>
      <div
        ref={colRef}
        onScroll={onScrollLoad}
        className="space-y-2 overflow-y-auto pr-1"
        style={{ maxHeight: "min(560px, calc(100vh - 230px))" }}
      >
        {leads.length === 0 ? (
          <div className="text-center py-10 text-slate-400 text-sm">No leads here 🎉</div>
        ) : (
          shown.map((lead) => (
            <BoardCard key={lead.id} lead={lead} tone={col} onAction={onAction} onOpen={onOpen} smart={smart} />
          ))
        )}
        {visible < leads.length && (
          <div className="text-center py-2 text-xs text-slate-400">Loading more… ({leads.length - visible} left)</div>
        )}
      </div>
    </div>
  );
};

function FollowUpQueue() {
  const [queue, setQueue] = useState({ high_priority: [], medium_priority: [], low_priority: [] });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [actionModal, setActionModal] = useState({ open: false, lead: null, type: null, script: "" });
  const [actionNote, setActionNote] = useState("");
  const [visible, setVisible] = useState({ high_priority: PAGE_SIZE, medium_priority: PAGE_SIZE, low_priority: PAGE_SIZE });
  const [sortBy, setSortBy] = useState(() => localStorage.getItem("fuq_sort_by") || "priority");
  const [contactedFilter, setContactedFilter] = useState(() => localStorage.getItem("fuq_contacted_filter") || "all");
  const navigate = useNavigate();
  const sl = useSmartLeads();
  const [sel, setSel] = useState({});
  const [draftIds, setDraftIds] = useState([]);
  const [staleIds, setStaleIds] = useState([]);
  const selIds = Object.keys(sel).filter((k) => sel[k]);

  const changeSort = (v) => { setSortBy(v); localStorage.setItem("fuq_sort_by", v); };
  const changeContacted = (v) => { setContactedFilter(v); localStorage.setItem("fuq_contacted_filter", v); };

  const pendingRef = useRef({});
  const colRefs = { high_priority: useRef(null), medium_priority: useRef(null), low_priority: useRef(null) };

  const fetchQueue = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/leads/follow-up-queue`);
      const data = response.data || {};
      const pendingIds = new Set(Object.keys(pendingRef.current));
      ["high_priority", "medium_priority", "low_priority"].forEach((k) => {
        data[k] = (data[k] || []).filter((l) => !pendingIds.has(l.id));
      });
      setQueue(withCounts(data));
    } catch (error) {
      console.error("Failed to fetch queue:", error);
      toast.error("Failed to load follow-up queue");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchQueue();
    const interval = setInterval(fetchQueue, 2 * 60 * 1000);
    return () => {
      clearInterval(interval);
      // Flush any pending (not-yet-committed) actions so they aren't lost on navigate.
      Object.values(pendingRef.current).forEach((e) => {
        clearTimeout(e.timer);
        axios.post(`${API}/leads/${e.lead.id}/actions`, e.payload).catch(() => {});
      });
      pendingRef.current = {};
    };
  }, [fetchQueue]);

  // Restore scroll position + page size when returning from a lead detail page.
  useEffect(() => {
    if (loading) return;
    const raw = sessionStorage.getItem("fuq_scroll");
    if (!raw) return;
    sessionStorage.removeItem("fuq_scroll");
    try {
      const s = JSON.parse(raw);
      if (s.visible) setVisible(s.visible);
      requestAnimationFrame(() => {
        Object.keys(colRefs).forEach((k) => {
          if (colRefs[k].current) colRefs[k].current.scrollTop = s[k] || 0;
        });
        window.scrollTo(0, s.win || 0);
      });
    } catch (e) { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  const filterLeads = (leads) => {
    let out = leads || [];
    if (contactedFilter === "replied") {
      out = out.filter((l) => sl.ai[l.id]?.paused);
    } else if (contactedFilter === "3plus") {
      out = out.filter((l) => (l.outreach_count ?? 0) >= 3);
    } else if (contactedFilter === "none") {
      out = out.filter((l) => (l.outreach_count ?? 0) === 0);
    }
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      out = out.filter((l) =>
        l.name?.toLowerCase().includes(term) ||
        l.email?.toLowerCase().includes(term) ||
        l.phone?.includes(term) ||
        l.pet_name?.toLowerCase().includes(term)
      );
    }
    // Sort: keep self-registered at the top no matter what, then user choice
    const sorted = [...out].sort((a, b) => {
      const aReg = a.self_registered ? 0 : 1;
      const bReg = b.self_registered ? 0 : 1;
      if (aReg !== bReg) return aReg - bReg;
      const aRep = sl.ai[a.id]?.paused ? 0 : 1;
      const bRep = sl.ai[b.id]?.paused ? 0 : 1;
      if (aRep !== bRep) return aRep - bRep;
      if (sortBy === "ai") return (sl.ai[b.id]?.score ?? -1) - (sl.ai[a.id]?.score ?? -1);
      const aSug = sl.ai[a.id]?.suggested ? 0 : 1;
      const bSug = sl.ai[b.id]?.suggested ? 0 : 1;
      if (sortBy === "priority" && aSug !== bSug) return aSug - bSug;
      if (sortBy === "age") return (b.days_since_created ?? 0) - (a.days_since_created ?? 0);
      if (sortBy === "age_new") return (a.days_since_created ?? 0) - (b.days_since_created ?? 0);
      if (sortBy === "contacts") return (b.outreach_count ?? 0) - (a.outreach_count ?? 0);
      // priority default: preserve backend order (already sorted)
      return 0;
    });
    return sorted;
  };

  const allLeads = useMemo(
    () => [...(queue.high_priority || []), ...(queue.medium_priority || []), ...(queue.low_priority || [])],
    [queue]
  );
  const getLead = (id) => {
    const l = allLeads.find((x) => x.id === id) || {};
    const a = sl.ai[id] || {};
    return { id, name: l.name || a.name, pet: l.pet_name || a.pet, phone: l.phone || a.phone,
      gingr: a.has_gingr ?? !!l.has_gingr, paused: !!a.paused, reply_snippet: a.reply_snippet, why: a.why, nba: a.nba };
  };
  const smart = {
    ai: sl.ai, sel, gingrUrl: queue.gingr_app_url,
    ob: Object.fromEntries(sl.outbox.map((o) => [o.lead_id, o.status])),
    toggle: (id) => setSel((s) => ({ ...s, [id]: !s[id] })),
    draft: (id) => setDraftIds([id]),
    resume: (id) => sl.resume(id),
  };

  // ── optimistic remove + undo ──────────────────────────────────────────
  const restoreLead = (lead, pkey) => {
    setQueue((prev) => {
      const arr = prev[pkey] || [];
      if (arr.some((l) => l.id === lead.id)) return prev;
      return withCounts({ ...prev, [pkey]: [lead, ...arr] });
    });
  };

  const commitAction = async (leadId) => {
    const entry = pendingRef.current[leadId];
    if (!entry) return;
    delete pendingRef.current[leadId];
    try {
      await axios.post(`${API}/leads/${leadId}/actions`, entry.payload);
    } catch (error) {
      toast.error("Couldn't save — lead restored to queue");
      restoreLead(entry.lead, entry.pkey);
    }
  };

  const undoRemoval = (leadId) => {
    const entry = pendingRef.current[leadId];
    if (!entry) return;
    clearTimeout(entry.timer);
    delete pendingRef.current[leadId];
    restoreLead(entry.lead, entry.pkey);
    toast.success("Restored to queue");
  };

  const removeLeadWithUndo = (lead, payload, message) => {
    const pkey = lead.priority === "high" ? "high_priority" : lead.priority === "medium" ? "medium_priority" : "low_priority";
    setQueue((prev) => withCounts({ ...prev, [pkey]: (prev[pkey] || []).filter((l) => l.id !== lead.id) }));
    const timer = setTimeout(() => commitAction(lead.id), UNDO_MS);
    pendingRef.current[lead.id] = { timer, lead, pkey, payload };
    toast(message, {
      description: "Tap Undo to put it back",
      duration: UNDO_MS,
      action: { label: "Undo", onClick: () => undoRemoval(lead.id) },
    });
  };

  const handleAction = (lead, actionType, script = "") => {
    if (actionType === "skip") {
      removeLeadWithUndo(lead, { type: "skip", notes: "Skipped from Follow-Up Queue" }, `Skipped ${lead.name}`);
      return;
    }
    if (actionType === "complete") {
      removeLeadWithUndo(lead, { type: "follow_up_complete", notes: "Marked complete from Follow-Up Queue" }, `Completed follow-up for ${lead.name}`);
      return;
    }
    setActionModal({ open: true, lead, type: actionType, script });
    setActionNote(script);
  };

  const submitAction = () => {
    if (!actionModal.lead) return;
    const lead = actionModal.lead;
    const type = actionModal.type;
    removeLeadWithUndo(lead, { type, notes: actionNote }, `${type.charAt(0).toUpperCase() + type.slice(1)} logged for ${lead.name}`);
    setActionModal({ open: false, lead: null, type: null, script: "" });
    setActionNote("");
  };

  // ── navigation with scroll-position memory ────────────────────────────
  const openDetail = (lead) => {
    sessionStorage.setItem("fuq_scroll", JSON.stringify({
      high_priority: colRefs.high_priority.current?.scrollTop || 0,
      medium_priority: colRefs.medium_priority.current?.scrollTop || 0,
      low_priority: colRefs.low_priority.current?.scrollTop || 0,
      win: window.scrollY,
      visible,
    }));
    navigate(`/leads/${lead.id}?from=queue`);
  };

  const onScrollLoad = (key) => (e) => {
    const el = e.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 220) {
      setVisible((v) => (v[key] >= (queue[key] || []).length ? v : { ...v, [key]: v[key] + PAGE_SIZE }));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="follow-up-queue-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl sm:text-[32px] font-extrabold tracking-tight text-gray-900" style={{ fontFamily: "Manrope" }}>Follow-Up Queue</h1>
          <p className="text-sm sm:text-base text-gray-500 mt-0.5"><span className="font-mono font-bold text-gray-900" style={{ fontFamily: "JetBrains Mono, monospace" }}>{queue.total_count || 0}</span> leads need your attention</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              type="text"
              placeholder="Search name, email, phone, pet…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 w-64"
              data-testid="queue-search"
            />
          </div>
          <Select value={sortBy} onValueChange={changeSort}>
            <SelectTrigger className="w-48 h-9" data-testid="queue-sort-select">
              <SelectValue placeholder="Sort" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="priority">Sort: Priority</SelectItem>
              <SelectItem value="age">Sort: Age (oldest)</SelectItem>
              <SelectItem value="age_new">Sort: Age (newest)</SelectItem>
              <SelectItem value="contacts">Sort: Contacts (most)</SelectItem>
              <SelectItem value="ai">Sort: AI score</SelectItem>
            </SelectContent>
          </Select>
          <Select value={contactedFilter} onValueChange={changeContacted}>
            <SelectTrigger className="w-44 h-9" data-testid="queue-contacted-filter">
              <div className="flex items-center gap-1.5">
                <Filter className="w-3.5 h-3.5 text-slate-400" />
                <SelectValue placeholder="Contacts" />
              </div>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All leads</SelectItem>
              <SelectItem value="none">Never contacted</SelectItem>
              <SelectItem value="3plus">Contacted 3+ times</SelectItem>
              <SelectItem value="replied">Replied (paused)</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={fetchQueue} variant="outline" size="sm">
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
        </div>
      </div>

      <SmartQueueBar sl={sl} onSent={fetchQueue} />

      {/* Legend */}
      <div className="flex items-center gap-4 text-xs text-slate-500 flex-wrap">
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-emerald-500" /> reachable now</span>
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-amber-400" /> outside their window</span>
        <span className="flex items-center gap-1"><Zap className="w-3.5 h-3.5 text-slate-400" /> hover a card for quick actions · click to open full details</span>
      </div>

      {/* Board */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-start">
        {COLUMNS.map((col) => (
          <BoardColumn
            key={col.key}
            col={col}
            leads={filterLeads(queue[col.key])}
            visible={visible[col.key]}
            colRef={colRefs[col.key]}
            onScrollLoad={onScrollLoad(col.key)}
            onAction={handleAction}
            onOpen={openDetail}
            smart={smart}
          />
        ))}
      </div>

      {selIds.length > 0 && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 rounded-full bg-slate-900 text-white shadow-lg px-4 py-2.5 hidden sm:flex items-center gap-3" data-testid="smart-batch-bar">
          <span className="text-sm font-medium" data-testid="smart-batch-count">{selIds.length} selected</span>
          <Button size="sm" className="bg-gradient-to-r from-amber-500 to-orange-600 rounded-full" onClick={() => setDraftIds(selIds)} data-testid="smart-batch-review-btn">
            <Sparkles className="h-4 w-4 mr-1.5" /> Review AI drafts
          </Button>
          <button className="text-slate-300 hover:text-white text-sm" onClick={() => setSel({})} data-testid="smart-batch-clear">Clear</button>
        </div>
      )}

      <LeadTextDrafts leads={filterLeads(allLeads)} sel={sel} setSel={setSel} sl={sl} invalidate={staleIds} gingrUrl={queue.gingr_app_url}
        onOpen={(id) => setDraftIds([id])} onSent={fetchQueue} />

      <SmartDraftDialog ids={draftIds} getLead={getLead} sl={sl}
        onClose={() => { setStaleIds(draftIds); setDraftIds([]); setSel({}); }} onChanged={fetchQueue} />

      {/* Action Modal */}
      <Dialog open={actionModal.open} onOpenChange={(open) => !open && setActionModal({ open: false, lead: null, type: null, script: "" })}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {actionModal.type === "call" && <Phone className="w-5 h-5 text-blue-600" />}
              {actionModal.type === "email" && <Mail className="w-5 h-5 text-green-600" />}
              {actionModal.type === "sms" && <MessageSquare className="w-5 h-5 text-purple-600" />}
              Log {actionModal.type?.charAt(0).toUpperCase() + actionModal.type?.slice(1)} for {actionModal.lead?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">Notes / Script Used</label>
              <Textarea
                value={actionNote}
                onChange={(e) => setActionNote(e.target.value)}
                placeholder={`Add notes about your ${actionModal.type}...`}
                rows={5}
                data-testid="action-note-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setActionModal({ open: false, lead: null, type: null, script: "" })}>Cancel</Button>
            <Button onClick={submitAction} data-testid="action-log-submit">
              <CheckCircle2 className="w-4 h-4 mr-2" /> Log Action
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default FollowUpQueue;
